import { Col, OverlayTrigger, Tooltip } from "react-bootstrap";
import { useIntl } from "react-intl";
import PropTypes from "prop-types";
import moment from "moment";

import { FreightModeBaseTypes } from "constants/";
import { formatWeight, decodeHTML } from "helpers/commonHelpers";
import { formatDate as parseDate, DateTimeFormat } from "helpers/dateTimeHelper";

function AdditionalInfo({ quotesData, costDetailsData, freightModeType, freightMode }) {
  const intl = useIntl();

  const { service_confirmation_data, road_freight_type } = quotesData?.service;
  const { attachments } = quotesData?.other;
  const { sailing, flights, vessels } = quotesData?.schedule;
  const { quote_title, tracking_number } = quotesData?.general;

  const displayFirstColumn = attachments.length > 0 || tracking_number || quote_title;

  const item = (label, value, value2, testId) => (
    <div key={label} className="d-flex flex-column" style={{ marginBottom: "16px" }}>
      <div className="font-b8 text-secondary mb-1 additional-info-title">{label}</div>
      <div className="font-b2" data-testid={testId || undefined}>
        <div
          style={{
            maxWidth: "500px",
            whiteSpace: "normal",
            wordWrap: "break-word",
            overflowWrap: "break-word",
          }}
        >
          {typeof value === "string" ? decodeHTML(value) : value} {value2 && `- ${value2}`}
        </div>
      </div>
    </div>
  );

  const formatDate = (date) => {
    if (!moment(date).isValid())
      return intl.formatMessage({ id: "general.no_date_available", defaultMessage: "No date available" });
    return moment(date).format("DD MMM YYYY - HH:mm");
  };

  const renderVesselData = (vessel) => {
    if (!vessel.departure_port_name && !vessel.arrival_port_name) return;
    return (
      <div>
        {item("Vessel Departure", vessel.departure_port_name, parseDate(vessel.departure, DateTimeFormat.dateFormat))}
        {item("Vessel Arrival", vessel.arrival_port_name, parseDate(vessel.arrival, DateTimeFormat.dateFormat))}
        {vessel.vessel_number && item("Vessel Number", vessel.vessel_number)}
      </div>
    );
  };

  const renderVesselsData = (vesselsList) => {
    if (!vesselsList || vesselsList.length === 0) return null;
    const showIndex = vesselsList.length > 1;
    const vesselDepartureLabel = intl.formatMessage({
      id: "quote.shipper.vesselDeparture",
      defaultMessage: "Vessel Departure",
    });
    const vesselArrivalLabel = intl.formatMessage({ id: "quote.shipper.vesselArrival", defaultMessage: "Vessel Arrival" });
    const vesselNumberLabel = intl.formatMessage({ id: "quote.shipper.vesselNumber", defaultMessage: "Vessel Number" });
    return vesselsList.map((vessel, i) => (
      <div key={vessel.vessel_number || i}>
        {item(
          `${vesselDepartureLabel} ${showIndex ? `#${i + 1}` : ""}`,
          vessel.departure_port_name,
          parseDate(vessel.delivery_date, DateTimeFormat.dateFormat),
        )}
        {item(
          `${vesselArrivalLabel} ${showIndex ? `#${i + 1}` : ""}`,
          vessel.arrival_port_name,
          parseDate(vessel.vessel_arrival_date, DateTimeFormat.dateFormat),
        )}
        {vessel.vessel_number && item(`${vesselNumberLabel} ${showIndex ? `#${i + 1}` : ""}`, vessel.vessel_number)}
      </div>
    ));
  };

  const renderOnTimePerformance = (performance) => {
    if (!performance.on_time) return "";
    const formattedOnTime = (performance.on_time * 100).toFixed(2);
    return (
      <div>
        <OverlayTrigger
          placement="top"
          delay={{ show: 50, hide: 50 }}
          overlay={<Tooltip id="button-tooltip">Based on {performance.observations} observations</Tooltip>}
        >
          <div className="d-inline-block">(on time: {formattedOnTime}%)</div>
        </OverlayTrigger>
      </div>
    );
  };

  const renderFlightData = (flights) => {
    if (!flights) return;
    return flights.map((flight, i) => {
      let index = false;
      if (flights.length > 1) index = true;
      const flightDepartureDate = freightMode === "Air charter" ? "" : formatDate(flight.departure);
      const flightArrivalDate = freightMode === "Air charter" ? "" : formatDate(flight.arrival);
      return (
        <div>
          {item(`Departure Airport ${index ? `#${i + 1}` : ""}`, flight.departure_airport, flightDepartureDate)}
          {item(`Arrival Airport ${index ? `#${i + 1}` : ""}`, flight.arrival_airport, flightArrivalDate)}
          <div>
            {flight.flight_number && (
              <div className="d-flex flex-column" style={{ marginBottom: "16px" }}>
                <div className="font-b8 text-secondary">{`Flight Number ${index ? `#${i + 1}` : ""}`}</div>
                <div className="d-flex font-b2">
                  <div className="mr-1">{`${flight.airline}${flight.flight_number}`}</div>
                  <div> {renderOnTimePerformance(flight.performance)}</div>
                </div>
              </div>
            )}
          </div>
        </div>
      );
    });
  };

  const renderAdditionalCostDetails = (type) => {
    switch (type) {
      case FreightModeBaseTypes.AIR:
        return (
          <>
            {costDetailsData?.service_level?.details?.max_allowed_wt &&
              item(
                intl.formatMessage({
                  id: "quote-request.costDetailOBCMaxAllowWeight",
                  defaultMessage: "Max allowed weight",
                }),
                costDetailsData?.service_level?.details?.max_allowed_wt,
              )}
            {costDetailsData?.service_level?.details?.total_flight_duration &&
              item(
                intl.formatMessage({
                  id: "quote-request.costDetailAirCarterTotalFlightDuration",
                  defaultMessage: "Total flight duration",
                }),
                costDetailsData?.service_level?.details?.total_flight_duration,
              )}
            {costDetailsData?.service_level?.details?.ptc &&
              item(
                intl.formatMessage({
                  id: "quote-request.costDetailAirCarterPTC",
                  defaultMessage: "Positioning time from confirmation",
                }),
                costDetailsData?.service_level?.details?.ptc,
              )}
            {costDetailsData?.service_level?.details?.aircraft_type &&
              item(
                intl.formatMessage({
                  id: "quote-request.costDetailAirCarterAircraftType",
                  defaultMessage: "Aircraft type",
                }),
                costDetailsData?.service_level?.details?.aircraft_type,
              )}
            {costDetailsData?.service_level?.details?.maximum_payload &&
              item(
                intl.formatMessage({
                  id: "quote-request.costDetailAirCarterMaximumPayload",
                  defaultMessage: "Maximum payload",
                }),
                costDetailsData?.service_level?.details?.maximum_payload,
              )}

          </>
        );
      case FreightModeBaseTypes.ROAD:
        return (
          <>
            {costDetailsData?.service_level?.details?.max_wt > 0 &&
              item(
                intl.formatMessage({
                  id: "quote-request.costDetailRoadMaxWeight",
                  defaultMessage: "Maximum weight",
                }),
                formatWeight(
                  costDetailsData?.service_level?.details?.max_wt,
                  costDetailsData?.service_level?.details?.max_wt_unit,
                ),
              )}

            {costDetailsData?.service_level?.details?.max_dimension > 0 &&
              item(
                intl.formatMessage({
                  id: "quote-request.costDetailRoadFreightMaximumDimension",
                  defaultMessage: "Maximum Dimensions",
                }),
                costDetailsData?.service_level?.details?.max_dimension,
              )}
          </>
        );
      default:
        return <div />;
    }
  };

  return (
    <div className="d-flex">
      {displayFirstColumn && (
        <Col>
          {quote_title && <div>{item("Quote title", quote_title)}</div>}
          {tracking_number && <div>{item("Tracking number", tracking_number)}</div>}
          {road_freight_type && <div>{item("Road freight type", road_freight_type)}</div>}
          <div className="d-flex flex-column" style={{ marginBottom: "16px" }}>
            {attachments.length > 0 && (
              <>
                <div className="text-secondary" style={{ fontSize: "10px" }}>
                  Attachments
                </div>
                {attachments.map((attachment) => (
                  <a
                    href={attachment.file_url}
                    key={attachment.file_url}
                    target="_blank"
                    style={{ fontSize: "11px", width: "170px", whiteSpace: "initial" }}
                    rel="noreferrer"
                  >
                    {attachment.filename}
                  </a>
                ))}
              </>
            )}
          </div>
        </Col>
      )}

      {service_confirmation_data.length > 0 && (
        <Col>
          {service_confirmation_data.map(
            ({ is_available, question_response, service_question, service_type, why_not_available, selected_incoterm }) => {
              let label;
              if (service_type === "Multiple choice") label = service_question;
              else if (service_question !== "") label = service_question;
              else label = service_type;

              let value;
              if (["FREE_TEXT", "MULTIPLE"].includes(service_type)) value = question_response;
              else if (service_type === "YESNO") value = is_available;
              else if (service_question !== "") value = question_response;
              else value = is_available;

              const additionalValue =
                why_not_available && selected_incoterm
                  ? `${why_not_available}, ${selected_incoterm}`
                  : why_not_available || selected_incoterm;
              return item(label, value, additionalValue);
            },
          )}
        </Col>
      )}

      <Col>
        {sailing && renderVesselData(sailing)}
        {vessels && vessels.length > 0 && renderVesselsData(vessels)}
        {flights && renderFlightData(flights)}
        {costDetailsData && renderAdditionalCostDetails(freightModeType)}
      </Col>
    </div>
  );
}

AdditionalInfo.propTypes = {
  quotesData: PropTypes.instanceOf(Object).isRequired,
  costDetailsData: PropTypes.instanceOf(Object).isRequired,
  freightModeType: PropTypes.string.isRequired,
  freightMode: PropTypes.string.isRequired,
};

export default AdditionalInfo;
